import Button, { ButtonProps } from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "components/dialog";
import { useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { error } from "app/themes/colors";
import {
  selectConfirmDialogOnChange,
  selectConfirmDialogOpen,
  selectConfirmDialogContent,
  selectConfirmDialogTitle,
  selectConfirmDialogOnCancel,
  selectConfirmDialogOptions,
  resetConfirmDialog,
} from "./confirm-dialog.slice";

const DangerButton = styled(Button)<ButtonProps>(({ theme }) => ({
  color: theme.palette.getContrastText(error[600]),
  backgroundColor: error[700],
  "&:hover": {
    backgroundColor: error[800],
  },
}));

export interface ConfirmDialogOptions {
  actionButtonType?: string;
  actionButtonText?: string;
  actionButtonDisabled?: boolean;
  zIndex?: number;
}

export default function ConfirmDialog() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const confirmChangeDialogOpen = useAppSelector(selectConfirmDialogOpen);
  const onChange = useAppSelector(selectConfirmDialogOnChange);
  const changeTitle = useAppSelector(selectConfirmDialogTitle);
  const changeContent = useAppSelector(selectConfirmDialogContent);
  const onCancel = useAppSelector(selectConfirmDialogOnCancel);
  const options = useAppSelector(selectConfirmDialogOptions);

  const handleChange = useCallback(() => {
    if (onChange) {
      onChange();
    }
    dispatch(resetConfirmDialog({ title: changeTitle }));
  }, [dispatch, onChange, changeTitle]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    dispatch(resetConfirmDialog());
  }, [onCancel, dispatch]);

  return (
    <Dialog
      title={changeTitle || "Are you sure you want to continue?"}
      onClose={handleCancel}
      open={confirmChangeDialogOpen}
      dialogContentProps={{
        sx: {
          display: "flex",
          alignItems: "center",
          pb: "20px",
          flexDirection: "column",
        },
      }}
      sx={{
        zIndex: options?.zIndex ?? theme.zIndex.snackbar + 2
      }}
    >
      {changeContent && (
        <Typography component="div" pb={2} variant="body3" width="100%">
          {changeContent}
        </Typography>
      )}
      <DialogActions>
        <Button
          data-testid="confirm-dialog-cancel"
          variant="outlined"
          size="small"
          onClick={handleCancel}
        >
          Cancel
        </Button>

        {options?.actionButtonType === "danger" ? (
          <Box
            data-testid="confirm-dialog-yes"
            sx={{ ml: 2 }}
            onClick={handleChange}
          >
            <DangerButton size="small" disabled={options?.actionButtonDisabled}>
              {options?.actionButtonText ?? "Yes"}
            </DangerButton>
          </Box>
        ) : (
          <Button
            data-testid="confirm-dialog-yes"
            variant="contained"
            size="small"
            onClick={handleChange}
            disabled={options?.actionButtonDisabled}
          >
            {options?.actionButtonText ?? "Yes"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
